/**
 * <g-tabs> - Tabbed view container
 *
 * DaisyUI tablist with keyboard a11y. Each child element with a
 * `data-tab="<id>"` attribute is treated as a panel; the `tabs` JS
 * property declares the header (label + optional icon per tab). The
 * active tab is shown, others hidden.
 *
 * @example
 *   <g-tabs id="t" active="customers">
 *     <div data-tab="customers"> ...customers content... </div>
 *     <div data-tab="agents">    ...agents content...    </div>
 *   </g-tabs>
 *   <script>
 *     t.tabs = [
 *       { id: 'customers', label: 'Customers', icon: 'users' },
 *       { id: 'agents',    label: 'Agents',    icon: 'user-tie' },
 *     ];
 *     t.addEventListener('tab-change', e => console.log(e.detail.active));
 *   </script>
 *
 * Properties:
 *   .tabs   Array<{ id, label, icon? }>
 *
 * Attributes:
 *   active   tab id to show on first render (default = first tab in .tabs)
 *   variant  'box' (default) | 'border' | 'lift' — DaisyUI v5 tabs style
 *            (legacy names 'boxed', 'bordered', 'lifted' from DaisyUI v3/4
 *             are accepted and mapped to v5 equivalents for back-compat)
 *   size     'xs' | 'sm' | 'md' (default) | 'lg' | 'xl' — DaisyUI v5 tabs size
 *   fill     boolean — fill the parent's height: the tab strip stays pinned and
 *            the active panel scrolls beneath it. For a tab set that IS the body
 *            of a fixed-height container, e.g. a record editor modal whose tabs
 *            hold a form, a comment thread and a file list.
 *
 * Children:
 *   [data-tab="<id>"]   a panel
 *   [data-tabs-end]     content placed at the right end of the tab strip — a
 *                       Filter button or an action that applies to every tab:
 *                       <g-tabs><div data-tabs-end><g-filter …></g-filter></div>…
 *
 * Events:
 *   tab-change   detail: { active, previous }
 *
 * Keyboard a11y on the tablist:
 *   ArrowLeft / ArrowRight — cycle through tabs
 *   Home / End             — jump to first / last
 *   Enter / Space          — activate focused tab
 */

// DaisyUI v5 variant names. v3/v4 names ('bordered', 'boxed', 'lifted') get
// mapped to their v5 equivalents below — silently, so older specs that hard-
// code the old names still render correctly under v5.
const ALLOWED_VARIANTS = new Set(['box', 'border', 'lift']);
const VARIANT_LEGACY_MAP = { bordered: 'border', boxed: 'box', lifted: 'lift' };
const ALLOWED_SIZES = new Set(['xs', 'sm', 'md', 'lg', 'xl']);

// `fill` layout classes, toggled so the author's own classes are left alone.
const FILL_HOST = ['flex', 'flex-col', 'min-h-0', 'h-full', 'gap-3'];
const FILL_PANEL_HOST = ['flex-1', 'min-h-0'];
const FILL_PANEL = ['h-full', 'overflow-y-auto'];

export class GTabs extends HTMLElement {
  constructor() {
    super();
    this._tabs = [];
    this._active = null;
    this._renderScheduled = false;
    this._fillApplied = false;
  }

  static get observedAttributes() {
    return ['active', 'variant', 'size', 'fill'];
  }

  attributeChangedCallback(name, _old, value) {
    if (name === 'active' && value && value !== this._active) {
      this._active = value;
    }
    this.scheduleRender();
  }

  connectedCallback() {
    if (!this.hasAttribute('fill')) this.classList.add('block', 'space-y-4');

    // Move existing children (panels) into a stable wrapper so we can
    // freely re-render the tab header above them without trashing user content.
    // The strip's end slot: taken out before the panels are moved, kept
    // across re-renders (only the tab buttons are rebuilt).
    if (!this._end) {
      const end = Array.from(this.children).filter((el) => el.hasAttribute('data-tabs-end'));
      if (end.length) {
        this._end = document.createElement('div');
        this._end.className = 'flex items-center gap-2 shrink-0';
        for (const el of end) this._end.appendChild(el);
      }
    }

    if (!this._panelHost) {
      this._panelHost = document.createElement('div');
      // Move every existing child INTO _panelHost
      while (this.firstChild) {
        this._panelHost.appendChild(this.firstChild);
      }
      this.appendChild(this._panelHost);
    }

    // Tab header (rebuilt on every render)
    if (!this._header) {
      this._header = document.createElement('div');
      this.insertBefore(this._header, this._panelHost);
    }

    // Pick initial active
    if (!this._active) {
      const fromAttr = this.getAttribute('active');
      this._active = fromAttr || this._firstTabId();
    }

    this.scheduleRender();
    this._header.addEventListener('keydown', this._handleKeydown);
    this._header.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this._header?.removeEventListener('keydown', this._handleKeydown);
    this._header?.removeEventListener('click', this._handleClick);
  }

  // ── Public API ──

  get tabs() { return this._tabs; }
  set tabs(t) {
    this._tabs = Array.isArray(t) ? t : [];
    if (!this._active && this._tabs.length) this._active = this._tabs[0].id;
    this.scheduleRender();
  }

  get active() { return this._active; }
  set active(id) {
    if (id === this._active) return;
    const previous = this._active;
    this._active = id;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('tab-change', {
      detail: { active: id, previous }, bubbles: true, composed: true,
    }));
  }

  // ── Internals ──

  // This tab set's panels: the DIRECT children carrying data-tab, as the
  // header comment says. A deep query also caught the panels of a <g-tabs>
  // nested inside one of ours — a page-level List / Pipeline switch reached
  // into each record modal and hid its Info panel, so the edit form opened
  // empty until the inner tab set re-rendered.
  _ownPanels() {
    if (!this._panelHost) return [];
    return Array.from(this._panelHost.children).filter((el) => el.hasAttribute('data-tab'));
  }

  _firstTabId() {
    if (this._tabs.length) return this._tabs[0].id;
    // Fallback: first [data-tab] in the panel host
    const first = this._ownPanels()[0];
    return first?.getAttribute('data-tab') || null;
  }

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._header) return;
    const rawVariant = this.getAttribute('variant');
    const mappedVariant = rawVariant && VARIANT_LEGACY_MAP[rawVariant] || rawVariant;
    const variant = ALLOWED_VARIANTS.has(mappedVariant) ? mappedVariant : 'box';
    const size = ALLOWED_SIZES.has(this.getAttribute('size')) ? this.getAttribute('size') : 'md';
    const sizeCls = size === 'md' ? '' : `tabs-${size}`;

    const tabsToRender = this._tabs.length ? this._tabs : this._inferTabsFromPanels();
    const tabButtons = tabsToRender.map(t => this._renderTabButton(t)).join('');

    // Layout. Default: a block that stacks header and panel with a gap and
    // grows with its content. `fill`: a flex column that takes the parent's
    // height — header pinned, panel host takes the rest, active panel scrolls.
    // Classes are only touched when the mode CHANGES, so an author's own
    // classes on the host (`flex-1 min-h-0`, say) survive either way.
    const fill = this.hasAttribute('fill');
    const wasFill = this._fillApplied;
    if (fill !== wasFill) {
      if (fill) this.classList.remove('block', 'space-y-4');
      else this.classList.add('block', 'space-y-4');
      for (const c of FILL_HOST) this.classList.toggle(c, fill);
      if (this._panelHost) for (const c of FILL_PANEL_HOST) this._panelHost.classList.toggle(c, fill);
      this._fillApplied = fill;
    }

    // Tint the .tabs container itself (full width, primary at low opacity)
    // so the toolbar reads as a colored band regardless of variant. The
    // active-tab pill (provided by tabs-box / tabs-border / tabs-lift)
    // stands out on top of the tint.
    if (this._end) {
      this._header.removeAttribute('role');
      if (!this._strip) this._strip = document.createElement('div');
      this._strip.setAttribute('role', 'tablist');
      // One tinted band: the tab buttons, then the end slot on the right.
      this._header.className = `${fill ? 'shrink-0 ' : ''}flex items-center gap-2 bg-primary/10 rounded-box pr-2`;
      this._strip.className = `tabs tabs-${variant} ${sizeCls} flex-1 min-w-0 bg-transparent`;
      this._strip.innerHTML = tabButtons;
      if (this._strip.parentNode !== this._header) this._header.replaceChildren(this._strip, this._end);
    } else {
      this._header.setAttribute('role', 'tablist');
      this._header.className = fill ? 'shrink-0' : '';
      this._header.innerHTML = `<div class="tabs tabs-${variant} ${sizeCls} w-full bg-primary/10">${tabButtons}</div>`;
    }

    // Show active panel, hide others
    const panels = this._ownPanels();
    panels.forEach(panel => {
      const id = panel.getAttribute('data-tab');
      const isActive = id === this._active;
      panel.style.display = isActive ? '' : 'none';
      if (fill || wasFill) for (const c of FILL_PANEL) panel.classList.toggle(c, fill && isActive);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }

  _renderTabButton(tab) {
    const isActive = tab.id === this._active;
    const icon = tab.icon
      ? `<i class="fa-regular fa-${escapeHtml(tab.icon)} mr-1.5"></i>`
      : '';
    const cls = `tab ${isActive ? 'tab-active' : ''}`.trim();
    return `<button
      type="button"
      role="tab"
      data-tab-id="${escapeHtml(tab.id)}"
      class="${cls}"
      aria-selected="${isActive}"
      aria-controls="tab-panel-${escapeHtml(tab.id)}"
      tabindex="${isActive ? '0' : '-1'}"
    >${icon}${escapeHtml(tab.label || tab.id)}</button>`;
  }

  _inferTabsFromPanels() {
    const panels = this._ownPanels();
    return Array.from(panels).map(p => ({
      id: p.getAttribute('data-tab'),
      label: p.getAttribute('data-label') || p.getAttribute('data-tab'),
      icon: p.getAttribute('data-icon') || null,
    }));
  }

  _handleClick = (e) => {
    const btn = e.target.closest('[data-tab-id]');
    if (!btn || !this._header.contains(btn)) return;
    this.active = btn.getAttribute('data-tab-id');
  };

  _handleKeydown = (e) => {
    // Keys typed into the end slot (a filter's search box) are not tab moves.
    if (this._end && this._end.contains(e.target)) return;
    const list = this._tabs.length ? this._tabs : this._inferTabsFromPanels();
    if (!list.length) return;
    const ids = list.map(t => t.id);
    const idx = ids.indexOf(this._active);
    let nextIdx = null;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % ids.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + ids.length) % ids.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = ids.length - 1;
    else if (e.key === 'Enter' || e.key === ' ') {
      const focused = document.activeElement?.closest('[data-tab-id]');
      if (focused) {
        e.preventDefault();
        this.active = focused.getAttribute('data-tab-id');
      }
      return;
    }
    if (nextIdx == null) return;
    e.preventDefault();
    this.active = ids[nextIdx];
    // Move focus to the newly-active tab button
    requestAnimationFrame(() => {
      const next = this._header?.querySelector(`[data-tab-id="${ids[nextIdx]}"]`);
      next?.focus();
    });
  };
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-tabs')) {
  customElements.define('g-tabs', GTabs);
}
